<template>
  <form
    class="sForm"
    :class="[labelPosition ? 'sForm--label-' + labelPosition : '',
             { 'sForm--inline': inline }]"
    @submit.prevent="$emit('submit')">
    <slot/>
  </form>
</template>
<script>
import objectAssign from '../../utils/merge'

export default {
  name: 'sForm',
  componentName: 'sForm',
  provide() {
    return {
      sForm: this
    }
  },
  data() {
    return {
      fields: [],
      potentialLabelWidthArr: []
    }
  },
  props: {
    /**
     * данные компонента формы
     */
    model: {
      type: Object,
    },
    /**
     * Признак блокировки всей формы
     */
    disabled: {
      type: Boolean,
    },
    /**
     * правила проверки формы
     */
    rules: {
      type: Object,
    },
    /**
     * является ли форма встроенной
     */
    inline: {
      type: Boolean,
    },
    /**
     * положение метки. Если установлено значение 'left' или 'right', также требуется свойство label-width.
     */
    labelPosition: {
      type: String,
      default: 'right',
      validator: function (value) {
        return ['left', 'right', 'top'].indexOf(value) !== -1
      }
    },
    /**
     * ширина метки, например '50px'. Все прямые дочерние элементы формы унаследуют это значение. Поддерживается автоматическая ширина.
     */
    labelWidth: {
      type: String,
    },
    /**
     * суффикс метки
     * результат `label${labelSuffix}`
     */
    labelSuffix: {
      type: String,
      default: ''
    },
    /**
     * следует ли отображать сообщение об ошибке в элементе формы
     */
    inlineMessage: {
      type: Boolean,
      default: false
    },
    /**
     * показывать ли сообщение об ошибке
     */
    showMessage: {
      type: Boolean,
      default: true
    },
    /**
     * запускать ли проверку при изменении правил
     */
    validateOnRuleChange: {
      type: Boolean,
      default: true
    },
    /**
     * следует ли скрывать красную звездочку (звездочку)
     */
    hideRequiredAsterisk: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    rules() {
      this.fields.forEach(field => {
        field.removeValidateEvents()
        field.addValidateEvents()
      })
      if (this.validateOnRuleChange) {
        this.validate(() => {})
      }
    }
  },
  computed: {
    autoLabelWidth() {
      if (!this.potentialLabelWidthArr.length) { return 0 }
      const max = Math.max(...this.potentialLabelWidthArr)
      return max ? `${max}px` : ''
    }
  },
  created() {
    this.$on('s.form.addField', (field) => {
      if (field) {
        this.fields.push(field)
      }
    })
    this.$on('s.form.removeField', (field) => {
      if (field.prop) {
        this.fields.splice(this.fields.indexOf(field), 1)
      }
    })
  },
  methods: {
    /**
     * сбросить все поля и удалить результат проверки
     */
    resetFields() {
      if (!this.model) {
        console.warn('[Form] Для работы resetFields требуется модель.')
        return
      }
      this.fields.forEach(field => {
        field.resetField()
      })
    },
    /**
     * очистить сообщение проверки для определенных полей. Параметр - это имя свойства или массив имен свойств элементов формы,
     * сообщения проверки которых будут удалены. Если этот параметр не указан, сообщения проверки всех полей будут очищены.
     */
    clearValidate(props = []) {
      const fields = props.length
        ? (typeof props === 'string'
          ? this.fields.filter(field => props === field.prop)
          : this.fields.filter(field => props.indexOf(field.prop) > -1)
        ) : this.fields
      fields.forEach(field => {
        field.clearValidate()
      })
    },
    /**
     * проверить всю форму. Принимает callback как параметр.
     * После проверки callback будет выполнен с двумя параметрами: логическим значением, указывающим, прошла ли проверка, и объектом,
     * содержащим все поля, не прошедшие проверку. Возвращает promise, если callback пропущен
     * onlyIfChange - проверяем только те свойства, в которых изменилось значение
     */
    validate(callback, onlyIfChange = false) {
      if (!this.model) {
        console.warn('[Form]model необходима для валидации для работы!')
        callback(true)
        return
      }
      let promise
      if (typeof callback !== 'function' && window.Promise) {
        promise = new window.Promise((resolve, reject) => {
          callback = function(valid) {
            valid ? resolve(valid) : reject(valid)
          }
        })
      }
      let valid = true
      let count = 0
      if (this.fields.length === 0 && callback) {
        callback(true)
      }
      let invalidFields = {}
      let scrollField
      this.fields.forEach(field => {
        field.validate(onlyIfChange, '', (message, fieldProp) => {
          if (message) {
            if (!scrollField) { scrollField = field }
            valid = false
          }
          invalidFields = objectAssign({}, invalidFields, fieldProp)
          if (typeof callback === 'function' && ++count === this.fields.length) {
            callback(valid, invalidFields)
          }
        })
      })
      if (scrollField && scrollField.$el) {
        scrollField.$el.scrollIntoView({block: 'center', behavior: 'smooth'})
      }
      if (promise) {
        return promise
      }
    },
    /**
     * проверить один или несколько элементов формы
     */
    /* eslint no-console:0 */
    validateField(props, cb) {
      props = [].concat(props)
      const fields = this.fields.filter(field => props.indexOf(field.prop) !== -1)
      if (!fields.length) {
        console.warn('пожалуйста, передайте правильный реквизит!')
        return
      }
      fields.forEach(field => {
        field.validate('', cb)
      })
    },
    getLabelWidthIndex(width) {
      const index = this.potentialLabelWidthArr.indexOf(width)
      if (index === -1) {
        throw new Error('[ElementForm]неожиданная ширина ', width)
      }
      return index
    },
    registerLabelWidth(val, oldVal) {
      if (val && oldVal) {
        const index = this.getLabelWidthIndex(oldVal)
        this.potentialLabelWidthArr.splice(index, 1, val)
      } else if (val) {
        this.potentialLabelWidthArr.push(val)
      }
    },
    deregisterLabelWidth(val) {
      const index = this.getLabelWidthIndex(val)
      this.potentialLabelWidthArr.splice(index, 1)
    }
  }
}
</script>
